import React, { useState, useEffect } from "react";
import { fetchPaperAssignmentRecordsAPI, deletePaperAssignmentRecordAPI } from "../../../paper-assignment/paperAssignmentAPI";

const thStyle = {
  padding: "13px 16px",
  fontSize: "0.78rem",
  fontWeight: 700,
  color: "#7A8490",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: "13px 16px", fontSize: "0.92rem" };

const formatGrade = (grade) => {
  if (!grade) return "";
  const g = parseInt(grade);
  if (g <= 6) return `초${g}`;
  if (g <= 9) return `중${g - 6}`;
  if (g <= 12) return `고${g - 9}`;
  return `${g}`;
};

const PaperAssignmentListView = ({ keyword }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const data = await fetchPaperAssignmentRecordsAPI();
      setRecords(data.results || data || []);
    } catch (err) {
      console.error("지필과제 로드 실패:", err);
      setRecords([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const filtered = keyword
    ? records.filter(
        (r) =>
          (r.student?.name || "").includes(keyword) ||
          (r.level?.name || "").includes(keyword) ||
          (r.class_content || "").includes(keyword)
      )
    : records;

  const handleDelete = async () => {
    if (!window.confirm(`선택한 ${selectedIds.length}개의 지필과제를 삭제하시겠습니까?`)) return;
    for (const id of selectedIds) {
      try {
        await deletePaperAssignmentRecordAPI(id);
      } catch (err) {
        console.error("삭제 실패:", err);
      }
    }
    setSelectedIds([]);
    loadRecords();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(filtered.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const isAllChecked = filtered.length > 0 && filtered.every((r) => selectedIds.includes(r.id));

  const getSubmissionStatus = (files) => {
    if (!files || files.length === 0) return { text: "과제 없음", color: "#9aa5ad" };
    const submitted = files.filter((f) => f.student_submission).length;
    if (submitted === files.length) return { text: `${submitted}/${files.length} 완료`, color: "#158A48" };
    if (submitted > 0) return { text: `${submitted}/${files.length} 진행중`, color: "#D4880F" };
    return { text: `0/${files.length} 미제출`, color: "#D14343" };
  };

  return (
    <div>
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#7A8490" }}>
          불러오는 중...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#7A8490" }}>
          지필과제가 없습니다.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <colgroup>
            <col style={{ width: "50px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "100px" }} />
            <col />
            <col style={{ width: "100px" }} />
            <col style={{ width: "80px" }} />
          </colgroup>
          <tbody>
            <tr style={{ textAlign: "left", borderBottom: "1.5px solid #0E141B" }}>
              <td style={{ ...thStyle, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={isAllChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </td>
              <td style={thStyle}>날짜</td>
              <td style={thStyle}>학생</td>
              <td style={thStyle}>학년</td>
              <td style={thStyle}>클래스</td>
              <td style={thStyle}>수업 내용</td>
              <td style={{ ...thStyle, textAlign: "center" }}>제출 현황</td>
              <td style={{ ...thStyle, textAlign: "center" }}>출석</td>
            </tr>
            {filtered.map((record) => {
              const status = getSubmissionStatus(record.homework_files);
              return (
                <tr key={record.id} style={{ borderBottom: "1px solid #E6EDEC" }}>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(record.id)}
                      onChange={() => toggleSelect(record.id)}
                    />
                  </td>
                  <td style={{ ...tdStyle, color: "#3A434E", fontSize: "0.86rem" }}>
                    {new Date(record.class_date || record.created_at).toLocaleDateString("ko-KR", {
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{record.student?.name || "-"}</td>
                  <td style={{ ...tdStyle, color: "#7A8490", fontSize: "0.86rem" }}>
                    {formatGrade(record.student?.grade)}
                  </td>
                  <td style={{ ...tdStyle, color: "#3A434E" }}>{record.level?.name || "-"}</td>
                  <td style={{
                    ...tdStyle,
                    color: "#0E141B",
                    maxWidth: "300px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    <span style={{
                      backgroundColor: "#FFF0E6",
                      color: "#D4880F",
                      padding: "3px 9px",
                      borderRadius: "7px",
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      marginRight: "8px",
                    }}>
                      지필과제
                    </span>
                    {record.class_content || "-"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: status.color,
                    }}>
                      {status.text}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {record.attendance !== null ? (
                      <span style={{
                        padding: "3px 9px",
                        borderRadius: "7px",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        backgroundColor: record.attendance ? "#E8F7EE" : "#FDE8E8",
                        color: record.attendance ? "#158A48" : "#D14343",
                      }}>
                        {record.attendance ? "출석" : "결석"}
                      </span>
                    ) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {selectedIds.length > 0 && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#2D2D2D",
          color: "#fff",
          padding: "20px",
          textAlign: "center",
          zIndex: 9999,
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "70%",
            margin: "0 auto",
          }}>
            <strong>지필과제 {selectedIds.length}개 선택됨</strong>
            <button
              onClick={handleDelete}
              style={{
                backgroundColor: "#FFFFFF",
                color: "#FF4444",
                border: "1px solid #EBEBEB",
                fontSize: "16px",
                padding: "10px 35px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              삭제하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperAssignmentListView;
